<template>
  <div class="devices-page">
    <!-- App header stays on top -->
    <AppHeader :title="headerTitle" class="app-header" />

    <!-- Loading overlay when all data is loading -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading device data...</div>
    </div>

    <!-- Error message -->
    <div v-else-if="error" class="error-container">
      <div class="error-message">{{ error }}</div>
      <button class="action-button" @click="fetchDeviceData">Retry</button>
    </div>

    <!-- Full page background with 2 rows and 3 columns containing content sections -->
    <div v-else class="container-fluid">
      <!-- Top row -->
      <div class="row h-40">
        <!-- Status column with component -->
        <div class="col-md-4 section-col p-0">
          <DeviceDetailsStatusHistory
              :device-id="device?.logical_device_number"
              :has-mpid="effectiveHasMpid"
              :device="device"
              :device-protocol="device?.protocol"
          />
        </div>

        <!-- Device details column -->
        <div class="col-md-4 section-col section-col-alt p-0">
          <DeviceDetailsInfo
              :device="device"
              :isLoading="deviceInfoLoading"
              :error="deviceError"
              @device-updated="handleDeviceUpdated"
              @reload="fetchAllData"
              @retry="fetchDeviceData"
          />
        </div>

        <!-- Metering point column -->
        <div class="col-md-4 section-col section-col-alt p-0">
          <DeviceDetailsMpid
              :meteringPoint="meteringPoint"
              :mpidValue="device ? device.mpid : ''"
              :isLoading="mpidLoading"
              :error="mpidError"
              :deviceId="device?.logical_device_number"
              :deviceType="device?.type || ''"
              :linkedMeteringPoint="linkedMeterMpData" :linkedMeterLDN="hasActiveMeter ? activeMeterLDN : ''"
              :hasActiveMeter="hasActiveMeter"
              @retry="fetchMeteringPoint"
              @mpid-attached="handleMpidAttached"
              @mpid-removed="handleMpidRemoved"
              @reload="fetchAllData"
          />
        </div>

      </div>

      <!-- Bottom row -->
      <div class="row h-40">
        <!-- Readings column - spans all 3 columns -->
        <div class="col-md-12 section-col section-col-alt p-0">
          <DeviceDetailsReadings
              :deviceId="device?.logical_device_number"
              :deviceProtocol="device?.protocol"
              :deviceSystemTitle="deviceSystemTitle"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars */
import AppHeader from './AppHeader.vue';
import DeviceDetailsInfo from './DeviceDetailsInfo.vue';
import DeviceDetailsMpid from './DeviceDetailsMpid.vue';
import DeviceDetailsReadings from './DeviceDetailsReadings.vue';
import DeviceDetailsReadingsChart from './DeviceDetailsReadingsChart.vue';
import DeviceDetailsStatusHistory from './DeviceDetailsStatus.vue';
import DeviceDetailsHistory from './DeviceDetailsHistory.vue';

export default {
  name: 'DeviceDetailsPage',
  components: {
    AppHeader,
    DeviceDetailsInfo,
    DeviceDetailsMpid,
    DeviceDetailsReadings,
    DeviceDetailsStatusHistory,

  },
  data() {
    return {
      // Main device data
      device: null,
      deviceInfoLoading: true,
      deviceError: '',
      // Metering point data
      meteringPoint: null,
      mpidLoading: true,
      mpidError: '',
      linkedMeterMpData: null,
      linkedMeterLDN: '',
      // Overall page state
      isLoading: true,
      error: ''
    };
  },
  computed: {
    headerTitle() {
      return this.device ? `Device: ${this.device.logical_device_number}` : 'Device Details';
    },
    isAddonDevice() {
      return this.device && this.device.type === 'ADDON';
    },
    isDLMSDevice() {
      return this.device && this.device.protocol && this.device.protocol.toLowerCase() === 'dlms';
    },
    // Get the correct system title for DLMS devices
    // DLMS devices use logical_device_number as system title (e.g., WTTS071225031917)
    deviceSystemTitle() {
      if (!this.device) return '';

      // For DLMS devices, the system title is typically stored in:
      // 1. device.system_title (if explicitly set)
      // 2. device.logical_device_number (common case where LDN = System Title)
      if (this.isDLMSDevice) {
        return this.device.system_title || this.device.logical_device_number || '';
      }

      // For non-DLMS devices, return logical_device_number
      return this.device.logical_device_number || '';
    },
    hasActiveMeter() {
      if (!this.device || !this.device.last_active_meter || !this.device.last_active_meter.length) {
        return false;
      }

      // Check if there's at least one meter without a totime or with empty totime
      return this.device.last_active_meter.some(meter =>
          !meter.to_time && !meter.totime
      );
    },
    effectiveHasMpid() {
      // If the current device is an ADDON type...
      if (this.isAddonDevice) {
        // ...then its effective MPID status depends on whether
        // it has an active meter AND that meter's MP data (linkedMeterMpData) was successfully fetched.
        return this.hasActiveMeter && !!this.linkedMeterMpData;
      }
      // Otherwise (for non-ADDON devices)...
      else {
        // ...its MPID status depends simply on whether it has its own 'mpid' property.
        return !!this.device?.mpid;
      }
    },
    activeMeterLDN() {
      if (!this.hasActiveMeter) return '';

      // Find current active meter (without totime)
      const currentActive = this.device.last_active_meter.find(meter =>
          !meter.to_time && !meter.totime
      );

      if (currentActive && currentActive.logical_device_number) {
        return currentActive.logical_device_number;
      }

      return '';
    }
  },
  watch: {
    '$route.params.ldn': {
      handler(newLdn, oldLdn) {
        // Only fetch if the LDN actually changed and exists
        if (newLdn && newLdn !== oldLdn) {
          console.log(`Route changed from ${oldLdn} to ${newLdn}`);
          this.fetchAllData();
        }
      },
      immediate: false
    }
  },

  created() {
    this.fetchAllData();
  },
  methods: {
    async fetchAllData() {
      this.isLoading = true;
      this.error = '';

      const ldn = this.$route.params.ldn;
      if (!ldn) {
        this.error = 'No device identifier provided';
        this.isLoading = false;
        return;
      }

      try {
        // Fetch device details first (readings are fetched by DeviceDetailsReadings component)
        await this.fetchDeviceData(ldn);

        // Fetch metering point if available
        if (this.device && this.device.mpid) {
          await this.fetchMeteringPoint();
        } else {
          this.mpidLoading = false;
        }

        // For ADDON devices, fetch linked meter and its metering point
        if (this.isAddonDevice && this.hasActiveMeter) {
          this.linkedMeterLDN = this.activeMeterLDN;
          if (this.linkedMeterLDN) {
            await this.fetchLinkedMeterData(this.linkedMeterLDN);
          }
        }
      } catch (error) {
        console.error('Error loading device details:', error);
        this.error = 'Failed to load device data. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchDeviceData(ldn) {
      this.deviceInfoLoading = true;
      this.deviceError = '';
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/devices/${ldn}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error(`Error fetching device: ${response.statusText}`);
        }
        this.device = await response.json();
        return this.device;
      } catch (error) {
        console.error('Error fetching device details:', error);
        this.deviceError = 'Failed to load device details';
        throw error;
      } finally {
        this.deviceInfoLoading = false;
      }
    },

    async fetchMeteringPoint() {
      const apiBaseUrl = process.env.VUE_APP_API_URL;
      if (!this.device?.mpid) {
        this.meteringPoint = null;
        return;
      }
      this.mpidLoading = true;
      this.mpidError = '';
      try {
        const response = await fetch(`${apiBaseUrl}/meteringpoints/${this.device.mpid}`, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch metering point: ${response.statusText}`);
        }
        this.meteringPoint = await response.json();
      } catch (error) {
        console.error('Error fetching metering point:', error);
        this.mpidError = 'Failed to fetch metering point details';
        this.meteringPoint = null;
      } finally {
        this.mpidLoading = false;
      }
    },

    async fetchLinkedMeterData(meterLDN) {
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/devices/${meterLDN}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching linked meter: ${response.statusText}`);
        }

        const meterData = await response.json();

        // If the meter has an MPID, fetch its metering point data
        if (meterData && meterData.mpid) {
          await this.fetchLinkedMeteringPoint(meterData.mpid);
        } else {
          this.linkedMeterMpData = null;
        }

        return meterData;
      } catch (error) {
        console.error('Error fetching linked meter data:', error);
        this.linkedMeterMpData = null;
        return null;
      }
    },

    async fetchLinkedMeteringPoint(mpid) {
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/meteringpoints/${mpid}`, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch linked metering point: ${response.statusText}`);
        }

        this.linkedMeterMpData = await response.json();
        return this.linkedMeterMpData;
      } catch (error) {
        console.error('Error fetching linked metering point:', error);
        this.linkedMeterMpData = null;
        return null;
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date time:', error);
        return 'Error';
      }
    },

    handleDeviceUpdated(updatedDevice) {
      // Store the old MPID value for comparison
      const oldMpid = this.device?.mpid;
      const oldType = this.device?.type;
      const wasAddon = oldType === 'ADDON';

      // Update the local device data with the updated version
      this.device = updatedDevice;

      // Check if device type changed (rare but possible)
      const typeChanged = oldType !== updatedDevice.type;

      // If MPID was removed completely (went from having a value to null/undefined)
      if (oldMpid && !updatedDevice.mpid) {
        this.meteringPoint = null;
        this.mpidLoading = false;
      }
      // If MPID changed or was added, refresh the metering point data
      else if (updatedDevice.mpid !== oldMpid) {
        this.fetchMeteringPoint();
      }

      // For ADDON devices or if type changed to ADDON, refresh linked meter data
      if ((this.isAddonDevice || typeChanged) && this.hasActiveMeter) {
        this.linkedMeterLDN = this.activeMeterLDN;
        if (this.linkedMeterLDN) {
          this.fetchLinkedMeterData(this.linkedMeterLDN);
        } else {
          this.linkedMeterMpData = null;
        }
      }
    },

    handleMpidAttached(newMpid) {
      console.log('MPID attached:', newMpid);
      // Update the device's mpid property
      if (this.device) {
        this.device.mpid = newMpid;
      }
      // Refresh metering point data
      this.fetchMeteringPoint();
    },

    handleMpidRemoved(updatedDevice) {
      console.log('MPID removed:', updatedDevice);

      // Clear the metering point data immediately
      this.meteringPoint = null;
      this.mpidLoading = false;
      this.mpidError = '';

      // Update the device data
      if (updatedDevice) {
        this.device = updatedDevice;
      } else if (this.device) {
        // If no updated device provided, clear the mpid manually
        this.device.mpid = null;
      }

      // For ADDON devices, also clear linked meter MP data if needed
      if (this.isAddonDevice) {
        this.linkedMeterMpData = null;
      }
    },

    openChangeMpidModal() {
      console.log('Opening change MPID modal');
    },

    openAttachMpidModal() {
      console.log('Opening attach MPID modal');
    },

    removeMpid(updatedDevice) {
      console.log('Device detached from MPID');

      if (updatedDevice) {
        // Explicitly reset meteringPoint when MPID is detached
        if (!updatedDevice.mpid) {
          this.meteringPoint = null;
        }

        // Update the local device data
        this.handleDeviceUpdated(updatedDevice);
      }

      // Always refresh all data to ensure everything is in sync
      this.fetchAllData();
    },

    navigateToDetailedReadings() {
      console.log('Navigating to detailed readings');
    }
  }
};
</script>
<style scoped>
.devices-page {
  height: 100vh;
  color: white;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;
}

/* Fixed header styling */
.app-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
}

/* Full height/width grid layout */
.container-fluid {
  height: 100vh;
  width: 100%;
  max-width: 100%;
  padding: 0;
  position: absolute;
  top: 0;
  left: 0;
}

.row {
  margin: 0;
  padding: 0;
}

/* Section columns styling */
.section-col {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px !important;
}

/* Section styling */
.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

.mt-4 {
  margin-top: 24px;
}

.device-details-grid {
  margin-top: 35px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.device-info-row {
  margin-bottom: 25px;
  display: flex;
  flex-wrap: wrap;
  gap: 30px; /* Space between each label-value pair */
}

/* Each label-value pair becomes a vertical group */
.device-info-group {
  display: flex;
  flex-direction: column;
  width: auto;
}

.device-info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-align: left;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.device-info-value {
  font-size: 11px;
  font-weight: 500;
}

.l {
  padding-left: 50px;
}

/* Loading states */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error state */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 10px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  gap: 20px;
}

.loading-text {
  color: white;
  font-size: 16px;
}

/* Error container */
.error-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  gap: 20px;
  padding: 20px;
  text-align: center;
}

@media (max-width: 767.98px) {
  .section-header {
    margin-top: 60px; /* Smaller margin for mobile */
  }

  .row {
    height: auto !important;
  }

  .section-col {
    padding: 15px !important;
  }

  .device-info-row {
    grid-template-columns: 1fr 1fr;
  }

  .l {
    padding-left: 30px;
  }
}


</style>